import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { AlertTriangle, BarChart3, Table2 } from "lucide-react";
import { rootRoute } from "./__root";
import { withPermission } from "../lib/with-permission";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { FormField } from "../components/ui/FormField/FormField";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { useToast } from "../lib/use-toast";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
	useLeaderboard,
	useSalesBreakdowns,
	useSalesOverview,
	useSalesTargets,
	useSaveSalesTarget,
	useDeleteSalesTarget,
	type Granularity,
	type LeaderboardEntry,
	type ProductGroupBy,
	type ReportCurrency,
} from "../hooks/use-reports";
import styles from "./Reports.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reports",
	component: withPermission(ReportsPage, "admin", "read"),
});

const iso = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfWeek = (d: Date) => {
	const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
	start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
	return start;
};
type Preset = "today" | "week" | "month" | "year" | "last12";
const startOfYear = (d: Date) => new Date(d.getFullYear(), 0, 1);

const amountFormatter = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const compactFormatter = new Intl.NumberFormat("tr-TR", {
	notation: "compact",
	maximumFractionDigits: 1,
});
const fmt = (v: number) => amountFormatter.format(v);
const fmtCompact = (v: number) => compactFormatter.format(v);
const fmtPercent = (v: number | null) =>
	v == null ? "-" : `%${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(v)}`;

function bucketLabel(bucket: string, granularity: Granularity): string {
	const date = new Date(bucket);
	if (Number.isNaN(date.getTime())) return bucket;
	switch (granularity) {
		case "day":
			return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
		case "week":
			return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
		case "year":
			return String(date.getFullYear());
		default:
			return date.toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
	}
}

function Tile({
	label,
	value,
	hint,
	meterPercent,
}: {
	label: string;
	value: string;
	hint?: string;
	meterPercent?: number | null;
}) {
	return (
		<div className={styles.tile}>
			<span className={styles.tileLabel}>{label}</span>
			<span className={styles.tileValue}>{value}</span>
			{meterPercent != null && (
				<div className={styles.meter}>
					<div
						className={[styles.meterFill, meterPercent >= 100 ? styles.meterFillGood : ""]
							.filter(Boolean)
							.join(" ")}
						style={{ width: `${Math.min(100, Math.max(0, meterPercent))}%` }}
					/>
				</div>
			)}
			{hint && <span className={styles.tileHint}>{hint}</span>}
		</div>
	);
}

interface TooltipEntry {
	name?: string;
	value?: number;
	color?: string;
	payload?: Record<string, unknown>;
}

function ChartTooltip({
	active,
	payload,
	label,
	formatter,
}: {
	active?: boolean;
	payload?: TooltipEntry[];
	label?: string;
	formatter: (v: number) => string;
}) {
	if (!active || !payload?.length) return null;
	return (
		<div className={styles.tooltip}>
			{label && <div className={styles.tooltipLabel}>{label}</div>}
			{payload.map((entry) => (
				<div key={entry.name} className={styles.tooltipRow}>
					<span>{entry.name}</span>
					<strong>{formatter(Number(entry.value ?? 0))}</strong>
				</div>
			))}
		</div>
	);
}

function Legend({ items }: { items: Array<{ label: string; color: string }> }) {
	if (items.length < 2) return null;
	return (
		<div className={styles.legend}>
			{items.map((item) => (
				<span key={item.label} className={styles.legendItem}>
					<span className={styles.legendSwatch} style={{ background: item.color }} />
					{item.label}
				</span>
			))}
		</div>
	);
}

function RankedBars({
	data,
	color,
	valueFormatter,
	emptyLabel,
}: {
	data: Array<{ name: string; value: number }>;
	color: string;
	valueFormatter: (v: number) => string;
	emptyLabel: string;
}) {
	if (data.length === 0) return <div className={styles.empty}>{emptyLabel}</div>;
	return (
		<div className={styles.chartWrapTall}>
			<ResponsiveContainer width="100%" height="100%">
				<BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 4 }}>
					<CartesianGrid horizontal={false} stroke="var(--chart-grid)" />
					<XAxis
						type="number"
						tickFormatter={fmtCompact}
						stroke="var(--chart-axis)"
						tick={{ fontSize: 11 }}
					/>
					<YAxis
						type="category"
						dataKey="name"
						width={150}
						stroke="var(--chart-axis)"
						tick={{ fontSize: 11 }}
						tickFormatter={(v: string) => (v.length > 22 ? `${v.slice(0, 21)}…` : v)}
					/>
					<Tooltip
						cursor={{ fill: "var(--color-bg-sunken)" }}
						content={<ChartTooltip formatter={valueFormatter} />}
					/>
					<Bar dataKey="value" name="Tutar" fill={color} radius={[0, 4, 4, 0]} barSize={16}>
						<LabelList
							dataKey="value"
							position="right"
							formatter={(v: unknown) => fmtCompact(Number(v ?? 0))}
							style={{ fill: "var(--color-fg-muted)", fontSize: 11 }}
						/>
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
}

function RankList({
	entries,
	emptyLabel,
	suffix,
}: {
	entries: LeaderboardEntry[];
	emptyLabel: string;
	suffix: string;
}) {
	const visible = entries.filter((e) => e.count > 0);
	if (visible.length === 0) return <div className={styles.empty}>{emptyLabel}</div>;
	return (
		<div className={styles.rankList}>
			{visible.map((entry, index) => (
				<div key={`${entry.userId ?? entry.name}-${index}`} className={styles.rankRow}>
					<span className={styles.rankIndex}>{index + 1}.</span>
					<span className={styles.rankName}>{entry.name}</span>
					<span className={styles.rankValue}>
						{fmt(entry.count)} {suffix}
					</span>
				</div>
			))}
		</div>
	);
}

function ReportsPage() {
	const { t } = useTranslation();
	const toast = useToast();
	const today = new Date();
	const [from, setFrom] = useState(iso(startOfYear(today)));
	const [to, setTo] = useState(iso(today));
	const [granularity, setGranularity] = useState<Granularity>("month");
	const [productGroupBy, setProductGroupBy] = useState<ProductGroupBy>("category");
	const [leaderPeriod, setLeaderPeriod] = useState<"week" | "month">("month");
	const [trendAsTable, setTrendAsTable] = useState(false);
	// Raporlar varsayılan olarak USD; TRY/EUR karşılığı da seçilebilir.
	const [reportCurrency, setReportCurrency] = useState<ReportCurrency>("USD");
	const [activePreset, setActivePreset] = useState<Preset | null>("year");

	const overview = useSalesOverview({ from, to, granularity, currency: reportCurrency });
	const breakdowns = useSalesBreakdowns({ from, to, productGroupBy, currency: reportCurrency });
	const leaderboard = useLeaderboard(leaderPeriod, reportCurrency);

	const series = useMemo(
		() =>
			(overview.data?.series ?? []).map((point) => ({
				...point,
				label: bucketLabel(point.bucket, granularity),
			})),
		[overview.data, granularity],
	);

	const applyPreset = (preset: Preset) => {
		const now = new Date();
		setActivePreset(preset);
		setTo(iso(now));
		if (preset === "today") {
			setFrom(iso(now));
			setGranularity("day");
		} else if (preset === "week") {
			setFrom(iso(startOfWeek(now)));
			setGranularity("day");
		} else if (preset === "month") {
			setFrom(iso(startOfMonth(now)));
			setGranularity("day");
		} else if (preset === "year") {
			setFrom(iso(startOfYear(now)));
			setGranularity("month");
		} else {
			setFrom(iso(new Date(now.getFullYear(), now.getMonth() - 11, 1)));
			setGranularity("month");
		}
	};

	const currency = overview.data?.currency ?? "TRY";
	const money = (v: number) => `${fmt(v)} ${currency}`;

	const wonLostData = overview.data
		? [
				{ name: t("offersWon"), value: overview.data.offers.won, color: "var(--chart-good)" },
				{ name: t("offersLost"), value: overview.data.offers.lost, color: "var(--chart-bad)" },
				{ name: t("offersPending"), value: overview.data.offers.pending, color: "var(--chart-4)" },
			]
		: [];

	return (
		<div className={styles.page}>
			<PageHeader title={t("salesReports")} subtitle={t("salesReportsDescription")} />

			<Card>
				<div className={styles.filters}>
					<div className={styles.filterGroup}>
						<span className={styles.filterLabel}>{t("startDate")}</span>
						<Input
							type="date"
							value={from}
							onChange={(e) => {
								setFrom(e.target.value);
								setActivePreset(null);
							}}
						/>
					</div>
					<div className={styles.filterGroup}>
						<span className={styles.filterLabel}>{t("endDate")}</span>
						<Input
							type="date"
							value={to}
							onChange={(e) => {
								setTo(e.target.value);
								setActivePreset(null);
							}}
						/>
					</div>
					<div className={styles.filterGroup}>
						<span className={styles.filterLabel}>{t("quickRange")}</span>
						<div className={styles.segmented}>
							{(
								[
									["today", t("today")],
									["week", t("thisWeek")],
									["month", t("thisMonth")],
									["year", t("thisYear")],
									["last12", t("last12Months")],
								] as Array<[Preset, string]>
							).map(([preset, label]) => (
								<button
									key={preset}
									type="button"
									className={[styles.segment, activePreset === preset ? styles.segmentActive : ""]
										.filter(Boolean)
										.join(" ")}
									onClick={() => applyPreset(preset)}
								>
									{label}
								</button>
							))}
						</div>
					</div>
					<div className={styles.filterGroup}>
						<span className={styles.filterLabel}>{t("currency")}</span>
						<div className={styles.segmented}>
							{(["USD", "TRY", "EUR"] as ReportCurrency[]).map((c) => (
								<button
									key={c}
									type="button"
									className={[styles.segment, reportCurrency === c ? styles.segmentActive : ""]
										.filter(Boolean)
										.join(" ")}
									onClick={() => setReportCurrency(c)}
								>
									{c}
								</button>
							))}
						</div>
					</div>
					<div className={styles.filterGroup}>
						<span className={styles.filterLabel}>{t("chartGranularity")}</span>
						<div className={styles.segmented}>
							{(["day", "week", "month", "year"] as Granularity[]).map((g) => (
								<button
									key={g}
									type="button"
									className={[styles.segment, granularity === g ? styles.segmentActive : ""]
										.filter(Boolean)
										.join(" ")}
									onClick={() => setGranularity(g)}
								>
									{t(`granularity_${g}`)}
								</button>
							))}
						</div>
					</div>
				</div>
			</Card>

			{overview.isLoading && <LoadingSpinner />}
			{overview.error && <div className={styles.empty}>{(overview.error as Error).message}</div>}

			{overview.data && (
				<>
					<div className={styles.rangeNote}>{t("kpiRangeNote")}</div>
					<div className={styles.tiles}>
						<Tile
							label={t("salesAmount")}
							value={money(overview.data.sales.amount)}
							hint={t("ordersCountedHint", { count: overview.data.sales.orderCount })}
						/>
						<Tile
							label={t("targetAchievement")}
							value={fmtPercent(overview.data.target.achievementPercent)}
							meterPercent={overview.data.target.achievementPercent}
							hint={
								overview.data.target.amount > 0
									? t("targetAmountHint", { amount: money(overview.data.target.amount) })
									: t("noTargetDefined")
							}
						/>
						<Tile
							label={t("averageOrderValue")}
							value={money(overview.data.sales.averageOrderValue)}
						/>
						<Tile label={t("offerCount")} value={fmt(overview.data.offers.count)} />
						<Tile
							label={t("winRate")}
							value={fmtPercent(overview.data.offers.winRate)}
							hint={t("wonLostHint", {
								won: overview.data.offers.won,
								lost: overview.data.offers.lost,
							})}
						/>
						<Tile label={t("wonOfferAmount")} value={money(overview.data.offers.wonAmount)} />
					</div>

					{overview.data.sales.missingRateCount > 0 && (
						<Card>
							<div className={styles.warning}>
								<AlertTriangle size={16} />
								{t("missingRateWarning", { count: overview.data.sales.missingRateCount })}
							</div>
							<div className={styles.tableWrap} style={{ marginTop: "var(--space-3)" }}>
								<table className={styles.table}>
									<thead>
										<tr>
											<th>{t("currency")}</th>
											<th className={styles.numeric}>{t("salesAmount")}</th>
											<th className={styles.numeric}>{t("orderCount")}</th>
											<th className={styles.numeric}>{t("missingRateColumn")}</th>
										</tr>
									</thead>
									<tbody>
										{overview.data.salesByCurrency.map((row) => (
											<tr key={row.currency}>
												<td>{row.currency}</td>
												<td className={styles.numeric}>{fmt(row.amount)}</td>
												<td className={styles.numeric}>{fmt(row.orderCount)}</td>
												<td className={styles.numeric}>{fmt(row.missingRateCount)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</Card>
					)}

					<Card>
						<div className={styles.cardHeader}>
							<span className={styles.cardTitle}>
								{t("salesTrend")} ({currency})
							</span>
							<Button size="sm" variant="ghost" onClick={() => setTrendAsTable((v) => !v)}>
								{trendAsTable ? <BarChart3 size={16} /> : <Table2 size={16} />}
								{trendAsTable ? t("showChart") : t("showTable")}
							</Button>
						</div>
						{trendAsTable ? (
							<div className={styles.tableWrap}>
								<table className={styles.table}>
									<thead>
										<tr>
											<th>{t("period")}</th>
											<th className={styles.numeric}>{t("salesAmount")}</th>
											<th className={styles.numeric}>{t("orderCount")}</th>
											<th className={styles.numeric}>{t("offerCount")}</th>
										</tr>
									</thead>
									<tbody>
										{series.map((point) => (
											<tr key={point.bucket}>
												<td>{point.label}</td>
												<td className={styles.numeric}>{fmt(point.amount)}</td>
												<td className={styles.numeric}>{fmt(point.orderCount)}</td>
												<td className={styles.numeric}>{fmt(point.offerCount)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div className={styles.chartWrap}>
								<ResponsiveContainer width="100%" height="100%">
									<AreaChart data={series} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
										<defs>
											<linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
												<stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.28} />
												<stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0.02} />
											</linearGradient>
										</defs>
										<CartesianGrid vertical={false} stroke="var(--chart-grid)" />
										<XAxis
											dataKey="label"
											stroke="var(--chart-axis)"
											tick={{ fontSize: 11 }}
											minTickGap={16}
										/>
										<YAxis
											tickFormatter={fmtCompact}
											stroke="var(--chart-axis)"
											tick={{ fontSize: 11 }}
											width={56}
										/>
										<Tooltip
											cursor={{ stroke: "var(--chart-axis)", strokeWidth: 1 }}
											content={<ChartTooltip formatter={money} />}
										/>
										<Area
											type="monotone"
											dataKey="amount"
											name={t("salesAmount")}
											stroke="var(--chart-1)"
											strokeWidth={2}
											fill="url(#salesFill)"
											dot={false}
											activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--color-bg-elevated)" }}
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>
						)}
					</Card>

					<div className={styles.grid2}>
						<Card>
							<div className={styles.cardHeader}>
								<span className={styles.cardTitle}>{t("offerCountTrend")}</span>
							</div>
							<div className={styles.chartWrap}>
								<ResponsiveContainer width="100%" height="100%">
									<BarChart data={series} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
										<CartesianGrid vertical={false} stroke="var(--chart-grid)" />
										<XAxis
											dataKey="label"
											stroke="var(--chart-axis)"
											tick={{ fontSize: 11 }}
											minTickGap={16}
										/>
										<YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} width={40} />
										<Tooltip
											cursor={{ fill: "var(--color-bg-sunken)" }}
											content={<ChartTooltip formatter={fmt} />}
										/>
										<Bar
											dataKey="offerCount"
											name={t("offerCount")}
											fill="var(--chart-2)"
											radius={[4, 4, 0, 0]}
											maxBarSize={28}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>
						</Card>

						<Card>
							<div className={styles.cardHeader}>
								<span className={styles.cardTitle}>{t("offerOutcomes")}</span>
							</div>
							<Legend items={wonLostData.map((d) => ({ label: d.name, color: d.color }))} />
							<div className={styles.chartWrap}>
								<ResponsiveContainer width="100%" height="100%">
									<BarChart data={wonLostData} margin={{ top: 16, right: 8, bottom: 4, left: 4 }}>
										<CartesianGrid vertical={false} stroke="var(--chart-grid)" />
										<XAxis dataKey="name" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
										<YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} width={40} />
										<Tooltip
											cursor={{ fill: "var(--color-bg-sunken)" }}
											content={<ChartTooltip formatter={fmt} />}
										/>
										<Bar
											dataKey="value"
											name={t("offerCount")}
											radius={[4, 4, 0, 0]}
											maxBarSize={64}
										>
											{wonLostData.map((entry) => (
												<Cell key={entry.name} fill={entry.color} />
											))}
											<LabelList
												dataKey="value"
												position="top"
												formatter={(v: unknown) => fmt(Number(v ?? 0))}
												style={{ fill: "var(--color-fg-muted)", fontSize: 11 }}
											/>
										</Bar>
									</BarChart>
								</ResponsiveContainer>
							</div>
						</Card>
					</div>
				</>
			)}

			{breakdowns.data && (
				<div className={styles.grid2}>
					<Card>
						<div className={styles.cardHeader}>
							<span className={styles.cardTitle}>{t("topSalespeople")}</span>
						</div>
						<RankedBars
							data={breakdowns.data.salespeople.map((s) => ({ name: s.name, value: s.amount }))}
							color="var(--chart-1)"
							valueFormatter={money}
							emptyLabel={t("noData")}
						/>
					</Card>

					<Card>
						<div className={styles.cardHeader}>
							<span className={styles.cardTitle}>{t("topCustomers")}</span>
						</div>
						<RankedBars
							data={breakdowns.data.customers.map((c) => ({ name: c.name, value: c.amount }))}
							color="var(--chart-2)"
							valueFormatter={money}
							emptyLabel={t("noData")}
						/>
					</Card>

					<Card>
						<div className={styles.cardHeader}>
							<span className={styles.cardTitle}>{t("salesByRegion")}</span>
						</div>
						<RankedBars
							data={breakdowns.data.regions.map((r) => ({ name: r.region, value: r.amount }))}
							color="var(--chart-3)"
							valueFormatter={money}
							emptyLabel={t("noData")}
						/>
					</Card>

					<Card>
						<div className={styles.cardHeader}>
							<span className={styles.cardTitle}>{t("salesByProductGroup")}</span>
							<div className={styles.segmented}>
								{(["category", "sap"] as ProductGroupBy[]).map((mode) => (
									<button
										key={mode}
										type="button"
										className={[styles.segment, productGroupBy === mode ? styles.segmentActive : ""]
											.filter(Boolean)
											.join(" ")}
										onClick={() => setProductGroupBy(mode)}
									>
										{t(mode === "category" ? "productGroupCategory" : "productGroupSap")}
									</button>
								))}
							</div>
						</div>
						<RankedBars
							data={breakdowns.data.productGroups.map((p) => ({
								name: p.productGroup,
								value: p.amount,
							}))}
							color="var(--chart-4)"
							valueFormatter={money}
							emptyLabel={t("noData")}
						/>
					</Card>
				</div>
			)}

			<Card>
				<div className={styles.cardHeader}>
					<span className={styles.cardTitle}>{t("leaderboard")}</span>
					<div className={styles.segmented}>
						{(["week", "month"] as const).map((p) => (
							<button
								key={p}
								type="button"
								className={[styles.segment, leaderPeriod === p ? styles.segmentActive : ""]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setLeaderPeriod(p)}
							>
								{t(p === "week" ? "thisWeek" : "thisMonth")}
							</button>
						))}
					</div>
				</div>
				{leaderboard.data && (
					<div className={styles.grid4}>
						<div>
							<div className={styles.tileLabel}>{t("topOfferCreators")}</div>
							<RankList
								entries={leaderboard.data.topOfferCreators}
								emptyLabel={t("noData")}
								suffix={t("pieceShort")}
							/>
						</div>
						<div>
							<div className={styles.tileLabel}>{t("topOrderCreators")}</div>
							<RankList
								entries={leaderboard.data.topOrderCreators}
								emptyLabel={t("noData")}
								suffix={t("pieceShort")}
							/>
						</div>
						<div>
							<div className={styles.tileLabel}>{t("topWonOffers")}</div>
							<RankList
								entries={leaderboard.data.topWon}
								emptyLabel={t("noData")}
								suffix={t("pieceShort")}
							/>
						</div>
						<div>
							<div className={styles.tileLabel}>{t("topLostOffers")}</div>
							<RankList
								entries={leaderboard.data.topLost}
								emptyLabel={t("noData")}
								suffix={t("pieceShort")}
							/>
						</div>
					</div>
				)}
			</Card>

			<SalesTargetsCard toast={toast} />
		</div>
	);
}

function SalesTargetsCard({ toast }: { toast: ReturnType<typeof useToast> }) {
	const { t } = useTranslation();
	const [year, setYear] = useState(new Date().getFullYear());
	const [month, setMonth] = useState(0);
	const [userId, setUserId] = useState("");
	const [amount, setAmount] = useState("");
	const targets = useSalesTargets(year);
	const saveTarget = useSaveSalesTarget();
	const deleteTarget = useDeleteSalesTarget();
	const { data: usersData } = useQuery({
		queryKey: ["admin-users", "for-targets"],
		queryFn: () =>
			api.get<{ items: Array<{ id: string; email: string; firstName: string; lastName: string }> }>(
				"/api/admin/users",
			),
	});
	const users = usersData?.items ?? [];

	const submit = async () => {
		const value = Number(amount);
		if (!Number.isFinite(value) || value < 0) {
			toast.showToast(t("invalidAmount"), "error");
			return;
		}
		try {
			await saveTarget.mutateAsync({
				year,
				month,
				userId: userId || null,
				amount: value,
				currency: "TRY",
			});
			setAmount("");
			toast.showToast(t("saved"), "success");
		} catch (err) {
			toast.showToast((err as Error).message, "error");
		}
	};

	const monthLabel = (m: number) =>
		m === 0
			? t("yearlyTarget")
			: new Date(2000, m - 1, 1).toLocaleDateString("tr-TR", { month: "long" });

	return (
		<Card>
			<div className={styles.cardHeader}>
				<span className={styles.cardTitle}>{t("salesTargets")}</span>
				<div className={styles.segmented}>
					<button type="button" className={styles.segment} onClick={() => setYear((y) => y - 1)}>
						‹
					</button>
					<button type="button" className={[styles.segment, styles.segmentActive].join(" ")}>
						{year}
					</button>
					<button type="button" className={styles.segment} onClick={() => setYear((y) => y + 1)}>
						›
					</button>
				</div>
			</div>

			<div className={styles.targetRow}>
				<FormField label={t("salesperson")}>
					<select
						value={userId}
						onChange={(e) => setUserId(e.target.value)}
						style={{ width: "100%" }}
					>
						<option value="">{t("companyWide")}</option>
						{users.map((u) => (
							<option key={u.id} value={u.id}>
								{[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}
							</option>
						))}
					</select>
				</FormField>
				<FormField label={t("period")}>
					<select
						value={month}
						onChange={(e) => setMonth(Number(e.target.value))}
						style={{ width: "100%" }}
					>
						{Array.from({ length: 13 }, (_, m) => (
							<option key={m} value={m}>
								{monthLabel(m)}
							</option>
						))}
					</select>
				</FormField>
				<FormField label={`${t("targetAmount")} (TRY)`}>
					<Input
						type="number"
						min="0"
						step="1000"
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
					/>
				</FormField>
				<Button onClick={submit} disabled={saveTarget.isPending}>
					{t("save")}
				</Button>
			</div>

			<div className={styles.tableWrap} style={{ marginTop: "var(--space-4)" }}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>{t("period")}</th>
							<th>{t("salesperson")}</th>
							<th className={styles.numeric}>{t("targetAmount")}</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{(targets.data ?? []).map((target) => (
							<tr key={target.id}>
								<td>{monthLabel(target.month)}</td>
								<td>{target.userName || target.userEmail || t("companyWide")}</td>
								<td className={styles.numeric}>
									{fmt(Number(target.amount))} {target.currency}
								</td>
								<td className={styles.numeric}>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => deleteTarget.mutate(target.id)}
										disabled={deleteTarget.isPending}
									>
										{t("delete")}
									</Button>
								</td>
							</tr>
						))}
						{(targets.data ?? []).length === 0 && (
							<tr>
								<td colSpan={4} className={styles.empty}>
									{t("noTargetDefined")}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</Card>
	);
}
